import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import {
  Route, // as BaseRoute,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Row, Col, Select, Tag, Dropdown, Menu, Button } from 'antd';
import { PictureOutlined, MoreOutlined } from '@ant-design/icons';

import { plantSelector, plantsSelector } from '../../store/plants';
import { PlantType, ReactorType } from '../../utils/types';
import Link from '../../components/Link';

import ReactorIndicator from './components/ReactorIndicator';
import {
  reactorsOfPlantSelector,
  reactorByPlantAndIndexSelector,
} from '../../store/reactors';
import { getCurrentDate } from '../../store/otherSelectors';

import ReactorDetails from './components/ReactorDetails';
import PlantPictures from './components/PlantPictures';

import './index.css';

const makeMapStateToPropsForReactor = () => {
  const getReactorByPlantAndIndex = createSelector(
    [
      state => state,
      (state, props) => props.currentDate,
      (state, props) => props.plantId,
      (state, props) => Number(props.reactorIndex),
    ],
    (state, currentDate, plantId, reactorIndex) =>
      reactorByPlantAndIndexSelector(
        {
          date: currentDate,
          plantId,
          reactorIndex,
        },
        state,
      ),
  );

  return (state, props) => {
    const reactor = getReactorByPlantAndIndex(state, props);
    return {
      reactor,
      setTab: tab =>
        props.goTo(
          `/plant/${reactor?.plantId}/${reactor?.reactorIndex}`,
          `#${tab}`,
        ),
    };
  };
};

const ReactorDetailsContainer = connect(makeMapStateToPropsForReactor())(
  ReactorDetails,
);

// Component to handle nested reactor details route
function ReactorDetailsRoute({ currentDate, currentTab, goTo, plantId }) {
  const params = useParams();
  return (
    <ReactorDetailsContainer
      currentTab={currentTab}
      currentDate={currentDate}
      goTo={goTo}
      plantId={plantId}
      reactorIndex={params.reactorIndex}
    />
  );
}

ReactorDetailsRoute.propTypes = {
  currentDate: PropTypes.string.isRequired,
  currentTab: PropTypes.string.isRequired,
  goTo: PropTypes.func.isRequired,
  plantId: PropTypes.string.isRequired,
};

function plantMenu(plant) {
  return {
    items: [
      {
        key: 'wikipedia',
        label: (
          <a href={plant.wikiLink} target="_blank" rel="noopener noreferrer">
            Wikipedia
          </a>
        ),
      },
      {
        key: 'asn',
        label: (
          <a href={plant.asnLink} target="_blank" rel="noopener noreferrer">
            ASN
          </a>
        ),
      },
      {
        key: 'edf',
        label: (
          <a href={plant.edfLink} target="_blank" rel="noopener noreferrer">
            EDF
          </a>
        ),
      },
    ],
  };
}

function PlantView(props) {
  const {
    currentDate,
    currentTab,
    plants,
    currentPlant,
    reactors,
    goTo,
    plantId,
  } = props;

  if (!currentPlant) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="PlantView">
      <Row>
        <Col span={24}>
          <div className="PlantView__firstRow">
            <div className="PlantView__firstRow__left">
              <Select
                value={currentPlant.id}
                size="large"
                onChange={id => goTo(`/plant/${id}`)}
                style={{ width: '10em' }}
              >
                {plants.map(plant => (
                  <Select.Option key={plant.id} value={plant.id}>
                    {plant.name}
                  </Select.Option>
                ))}
              </Select>
              <span>
                <Tag style={{ marginLeft: '8px' }}>
                  {currentPlant.coolingType === 'SEA' ? 'Mer' : 'Fleuve'}
                </Tag>
              </span>
            </div>
            <div className="PlantView__firstRow__right">
              <Link to={`/plant/${currentPlant.id}/pictures`}>
                <Button icon={<PictureOutlined />} style={{ marginRight: 5 }} />
              </Link>
              <Dropdown
                menu={plantMenu(currentPlant)}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          </div>

          <div className="PlantView__schema">
            <div>Production (MW)</div>
            <div className="PlantView__reactors">
              {reactors.map(reactor => (
                <Link
                  key={reactor.eicCode}
                  to={`/plant/${currentPlant.id}/${reactor.reactorIndex}`}
                >
                  <ReactorIndicator
                    reactor={reactor}
                    currentDate={currentDate}
                  />
                </Link>
              ))}
            </div>
            <div
              className="PlantView__cooling"
              style={{ width: reactors.length * 50 }}
            >
              {currentPlant.coolingPlace}
            </div>
          </div>
          <div className="PlantView__footer">
            <Routes>
              <Route
                path="pictures"
                element={<PlantPictures plant={currentPlant} />}
              />
              <Route
                path=":reactorIndex"
                element={
                  <ReactorDetailsRoute
                    currentTab={currentTab}
                    currentDate={currentDate}
                    goTo={goTo}
                    plantId={plantId}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <small>(Cliquer sur un réacteur pour plus de détails)</small>
                }
              />
            </Routes>
          </div>
        </Col>
      </Row>
    </div>
  );
}

PlantView.propTypes = {
  currentDate: PropTypes.string.isRequired,
  currentTab: PropTypes.string.isRequired,
  plants: PropTypes.arrayOf(PlantType).isRequired,
  currentPlant: PlantType,
  reactors: PropTypes.arrayOf(ReactorType).isRequired,
  goTo: PropTypes.func.isRequired,
  plantId: PropTypes.string.isRequired,
};

function PlantViewWithRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { plantId } = params;
  const currentDate = getCurrentDate(location);
  const currentTab = location.hash.substr(1);

  const goTo = (url, hash = '') => navigate(`${url}${location.search}${hash}`);

  return (
    <PlantViewConnected
      plantId={plantId}
      currentDate={currentDate}
      currentTab={currentTab}
      goTo={goTo}
      location={location}
      params={params}
    />
  );
}

// Create a factory function to generate stable mapStateToProps
const makeMapStateToProps = () => {
  // Create memoized selectors that remember their parameters
  const getPlantsForDate = createSelector(
    [state => state, (state, props) => props.currentDate],
    (state, currentDate) => plantsSelector(state, { date: currentDate }),
  );

  const getCurrentPlantForDateAndId = createSelector(
    [
      state => state,
      (state, props) => props.plantId,
      (state, props) => props.currentDate,
    ],
    (state, plantId, currentDate) =>
      plantSelector(state, { plantId, date: currentDate }),
  );

  const getReactorsForPlantAndDate = createSelector(
    [
      state => state,
      (state, props) => props.plantId,
      (state, props) => props.currentDate,
    ],
    (state, plantId, currentDate) =>
      reactorsOfPlantSelector(state, { plantId, date: currentDate }),
  );

  return (state, props) => {
    const { plantId, currentDate } = props;

    return {
      currentDate,
      currentTab: props.currentTab,
      plants: getPlantsForDate(state, props),
      currentPlant: getCurrentPlantForDateAndId(state, props),
      reactors: getReactorsForPlantAndDate(state, props),
      goTo: props.goTo,
      plantId: props.plantId,
      params: props.params,
    };
  };
};

const PlantViewConnected = connect(makeMapStateToProps)(PlantView);

export default PlantViewWithRouter;
